import { getOrderQuoteLockState } from '@/lib/order-quote'
import type { OrderQuoteLockControls, OrderQuoteLockInput } from '@/lib/order-quote'

export type PosCartLeaveAction = 'clear' | 'start-new' | 'save-first'

export type PosCartLeaveControls = OrderQuoteLockControls

// The saved state and quote controls this decision reads; the cart service is still JavaScript.
export interface PosCartLeaveContext extends OrderQuoteLockInput {
  saved_at?: string | null
}

/**
 * How staff leave the cart that is open on the POS.
 * - `clear`: delete it (a plain unpaid cart).
 * - `start-new`: leave it in place (it is in Draft Orders, or it is locked).
 * - `save-first`: it holds customer money and is not saved yet, so Save as Draft is the only way
 *   out. The recovery banner still catches one left by closing the tab.
 */
export function getPosCartLeaveAction(
  cart: PosCartLeaveContext | null | undefined
): PosCartLeaveAction {
  if (!cart) {
    return 'clear'
  }

  if (cart.saved_at) {
    return 'start-new'
  }

  const lockState = getOrderQuoteLockState(cart)

  // Never a dead end: without a way to save, the cart is left in place instead.
  if (lockState.holdsCustomerMoney && lockState.canSaveForLater) {
    return 'save-first'
  }

  return lockState.locked || !lockState.canDelete ? 'start-new' : 'clear'
}

// The slice of a router blocker's navigation this decision reads.
interface PosCartLeaveLocation {
  fullPath: string
  pathname: string
  search?: unknown
}

export interface PosCartLeaveNavigation {
  current: PosCartLeaveLocation
  next: PosCartLeaveLocation
  action?: string
}

function searchCartId(search: unknown): number {
  return typeof search === 'object' && search !== null && 'cartId' in search
    ? Number(search.cartId ?? 0)
    : 0
}

/**
 * Whether leaving an unsaved cart that holds a payment should ask staff first.
 * `paymentWindowAsking`: the payment window holds navigation itself, so staff are asked once.
 */
export function shouldAskBeforeLeavingCart(
  { current, next, action }: PosCartLeaveNavigation,
  { cartId, paymentWindowAsking }: { cartId?: number | null; paymentWindowAsking: boolean }
): boolean {
  // Signing out cannot be undone by staying, so it is never held up. Back to the login page is
  // not signing out: that page sends signed-in staff straight back to the POS without the cart.
  if (next.fullPath === '/login' && action !== 'BACK') {
    return false
  }

  if (paymentWindowAsking) {
    return false
  }

  // Search changes that keep this cart open are not leaving it. The path includes the organization,
  // so switching organizations with the same cart id still leaves the cart.
  return !(next.pathname === current.pathname && searchCartId(next.search) === Number(cartId ?? 0))
}
